import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { canSeeCrm, canSeeOutreach } from './constants/features';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ChatPage from './pages/ChatPage';
import SettingsPage from './pages/SettingsPage';
import ReceiptsPage from './pages/ReceiptsPage';
import ScansPage from './pages/ScansPage';
import QaValidatorPage from './pages/QaValidatorPage';
import SharedReportPage from './pages/SharedReportPage';
import SharesPage from './pages/SharesPage';
import UsagePage from './pages/UsagePage';
import CalculatorPage from './pages/CalculatorPage';
import UniboxPage from './outreach/pages/UniboxPage';
import MailboxesPage from './outreach/pages/MailboxesPage';
import ContactsPage from './outreach/pages/ContactsPage';
import CampaignsPage from './outreach/pages/CampaignsPage';
import LeadsPage from './outreach/pages/LeadsPage';
import AnalyticsPage from './outreach/pages/AnalyticsPage';
import OutreachSettingsPage from './outreach/pages/OutreachSettingsPage';
import CrmLeadsPage from './crm/pages/CrmLeadsPage';
import CrmLeadPage from './crm/pages/CrmLeadPage';
import CrmClientsPage from './crm/pages/CrmClientsPage';
import CrmClientPage from './crm/pages/CrmClientPage';
import CrmStatsPage from './crm/pages/CrmStatsPage';
import Layout from './components/Layout';
import NewVersionNotice from './components/NewVersionNotice';
import { AdminApp } from './admin';

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  // Where they were heading, so a deep link (the CRM link in a Telegram alert)
  // survives a login instead of dumping them on the dashboard.
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div className="spinner" />
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" state={{ from: location }} replace />;
}

function AdminRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!user?.is_superuser) {
    return <Navigate to="/" />;
  }

  return children;
}

function OutreachRoute({ children }) {
  const { user } = useAuth();
  return canSeeOutreach(user) ? children : <Navigate to="/" />;
}

function CrmRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return canSeeCrm(user) ? children : <Navigate to="/" />;
}

function App() {
  return (
    <>
      <NewVersionNotice />
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/r/:token" element={<SharedReportPage />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="chat/:chatId" element={<ChatPage />} />
        <Route path="scans" element={<ScansPage />} />
        <Route path="calculator" element={<CalculatorPage />} />
        <Route path="receipts" element={<ReceiptsPage />} />
        <Route path="qa-validator" element={<QaValidatorPage />} />
        <Route path="shares" element={<SharesPage />} />
        <Route path="outreach" element={<OutreachRoute><UniboxPage /></OutreachRoute>} />
        <Route path="outreach/mailboxes" element={<OutreachRoute><MailboxesPage /></OutreachRoute>} />
        <Route path="outreach/contacts" element={<OutreachRoute><ContactsPage /></OutreachRoute>} />
        <Route path="outreach/campaigns" element={<OutreachRoute><CampaignsPage /></OutreachRoute>} />
        <Route path="outreach/leads" element={<OutreachRoute><LeadsPage /></OutreachRoute>} />
        <Route path="outreach/analytics" element={<OutreachRoute><AnalyticsPage /></OutreachRoute>} />
        <Route path="outreach/settings" element={<OutreachRoute><OutreachSettingsPage /></OutreachRoute>} />
        <Route path="crm" element={<CrmRoute><CrmLeadsPage /></CrmRoute>} />
        <Route path="crm/leads/:leadId" element={<CrmRoute><CrmLeadPage /></CrmRoute>} />
        <Route path="crm/clients" element={<CrmRoute><CrmClientsPage /></CrmRoute>} />
        <Route path="crm/clients/:clientId" element={<CrmRoute><CrmClientPage /></CrmRoute>} />
        <Route path="crm/stats" element={<CrmRoute><CrmStatsPage /></CrmRoute>} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route
        path="/admin/*"
        element={
          <AdminRoute>
            <AdminApp />
          </AdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
}

export default App;
